import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/capacitor';
import { get } from 'lodash';
import {
  catchError,
  map,
  mergeMap,
  of,
  retry,
  startWith,
  switchMap,
  throwError,
  timer
} from 'rxjs';
import { Logger } from '@app/utils/logger';
import { AuthActions } from '@components/auth/auth.actions';
import { ReportType } from '@interfaces/schema';
import { QuestionnaireActiveActions } from './active.actions';
import { selectQuestionnaireActiveFormForSubmit } from './active.selectors';
import { QuestionnaireActiveService } from './active.service';

/**
 * @description Questionnaire Active Effects.
 * @exports
 * @class QuestionnaireActiveEffects
 */
@Injectable()
export class QuestionnaireActiveEffects extends QuestionnaireActiveService {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);
  private readonly logger = Logger.getInstance(QuestionnaireActiveEffects.name);

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => QuestionnaireActiveActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onFetchUserProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(QuestionnaireActiveActions.fetchUserProfile),
        switchMap(() => {
          // Check network status
          if (!navigator.onLine) {
            return of(
              QuestionnaireActiveActions.setLoading({
                key: 'fetchUserProfile',
                value: false
              })
            );
          }

          return this.fetchUserProfile().pipe(
            retry({
              count: 2,
              /**
               * @description Retry logic for network errors.
               * @param {unknown} error Error object.
               * @param {number} retryCount Current retry attempt count.
               * @returns {Observable<number>} Observable that emits a delay before retrying.
               */
              delay: (error, retryCount) => {
                if (this.isNetworkError(error) && retryCount < 2) {
                  this.logger.info(
                    `[QuestionnaireActiveEffects] Retrying onFetchUserProfile... Attempt ${retryCount + 1}`
                  );
                  return timer(1000 * (retryCount + 1));
                }
                return throwError(() => error as Error);
              }
            }),
            mergeMap((response) => {
              return [
                QuestionnaireActiveActions.setLoading({
                  key: 'fetchUserProfile',
                  value: false
                }),
                QuestionnaireActiveActions.setUserProfile({
                  value: get(response, 'data.results[0]', null)
                })
              ];
            }),
            catchError((error) => {
              this.logger.error(
                '[QuestionnaireActiveEffects] onFetchUserProfile Error:',
                error
              );
              Sentry.withScope((scope) => {
                scope.setTransactionName(
                  '[QuestionnaireActiveEffects] onFetchUserProfile'
                );
                Sentry.captureException(error, scope);
              });
              return of(
                QuestionnaireActiveActions.setLoading({
                  key: 'fetchUserProfile',
                  value: false
                }),
                QuestionnaireActiveActions.setUserProfile({
                  value: null
                })
              );
            }),
            startWith(
              QuestionnaireActiveActions.setLoading({
                key: 'fetchUserProfile',
                value: true
              })
            )
          );
        })
      );
    },
    { dispatch: true }
  );

  readonly onSubmit = createEffect(
    () => {
      return this.actions.pipe(
        ofType(QuestionnaireActiveActions.submit),
        concatLatestFrom(() => [
          this.store.select(selectQuestionnaireActiveFormForSubmit)
        ]),
        switchMap(([, form]) => {
          // Check network status
          if (!navigator.onLine) {
            return of(
              QuestionnaireActiveActions.setLoading({
                key: 'submit',
                value: false
              }),
              QuestionnaireActiveActions.setSubmissionResponse({
                success: false,
                message:
                  'No internet connection. Please check your network and try again.',
                response: null
              })
            );
          }

          return this.submit({
            data: {
              answerQuestion: form,
              email: this.session.getProfile()?.email ?? '',
              reportType: ReportType.Active
            }
          }).pipe(
            retry({
              count: 2,
              /**
               * @description Retry logic for network errors.
               * @param {unknown} error Error object.
               * @param {number} retryCount Current retry attempt count.
               * @returns {Observable<number>} Observable that emits a delay before retrying.
               */
              delay: (error, retryCount) => {
                if (this.isNetworkError(error) && retryCount < 2) {
                  this.logger.info(
                    `[QuestionnaireActiveEffects] Retrying onSubmit... Attempt ${retryCount + 1}`
                  );
                  return timer(1000 * (retryCount + 1));
                }
                return throwError(() => error as Error);
              }
            }),
            mergeMap((response) => [
              QuestionnaireActiveActions.setLoading({
                key: 'submit',
                value: false
              }),
              QuestionnaireActiveActions.setSubmissionResponse({
                success: true,
                message: 'Submission successful',
                response
              })
            ]),
            catchError((error) => {
              this.logger.error(
                '[QuestionnaireActiveEffects] onSubmit Error:',
                error
              );
              Sentry.withScope((scope) => {
                scope.setTransactionName(
                  '[QuestionnaireActiveEffects] onSubmit Error:'
                );
                Sentry.captureException(error, scope);
              });
              const errorMessage = this.getNetworkErrorMessage(error);
              return of(
                QuestionnaireActiveActions.setLoading({
                  key: 'submit',
                  value: false
                }),
                QuestionnaireActiveActions.setSubmissionResponse({
                  success: false,
                  message: errorMessage ?? 'Submission failed',
                  response: null
                })
              );
            }),
            startWith(
              QuestionnaireActiveActions.setLoading({
                key: 'submit',
                value: true
              })
            )
          );
        })
      );
    },
    { dispatch: true }
  );

  /**
   * @description Get a user-friendly error message based on the error object.
   * @private
   * @param {unknown} error Error object to analyze.
   * @returns {string}  {string}.
   * @memberof QuestionnaireActiveEffects
   */
  private getNetworkErrorMessage(error: any): string | null {
    if (!navigator.onLine || this.isNetworkError(error)) {
      return 'No internet connection. Please check your network and try again.';
    }
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    if (error.status === 408) {
      return 'Request timeout. Please try again.';
    }
    return null;
  }

  /**
   * @description Helper methods for network error handling.
   * @private
   * @param {unknown} error Error object to check.
   * @returns {boolean}  {boolean}.
   * @memberof QuestionnaireActiveEffects
   */
  private isNetworkError(error: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      (!navigator.onLine ||
        error.status === 0 ||
        error.status === 503 ||
        error.status === 504 ||
        error.name === 'NetworkError' ||
        error.message?.includes('Network')) ??
      error.message?.includes('fetch') ??
      error.message?.includes('Failed to fetch')
    );
  }
}
